import { BigNumber } from 'bignumber.js';

import type { ChainAddressId } from '@infinex/asset-config';

import type { PrecomputedBalances } from './precomputeBalances';

import type { AccountAddress } from '../../../../types';
import type {
  BalancesByChainAddressId,
  ChainAssetBalance,
  ChainNftAssetBalance,
  ChainNftAssetBalanceSummed,
  ChainNonPositionAssetBalance,
  ChainPositionChainAssetBalance,
  ChainPositionChainAssetBalanceSummed,
  ChainTokenAssetBalance,
  GroupedNftBalance,
  NftBalancesByGroupId,
  PositionBalancesByGroupIdByTypeByProtocolId,
  TokenBalancesByGroupId,
} from '../../balance';
import {
  addUniqueCustodyAddresses,
  addUniqueSources,
  addUniqueWalletIds,
  groupBalancesByGroupId,
  groupPositionsByProtocolIdByGroupId,
} from '../../utils';

const BALANCES_STALE_TIME = 1000 * 60 * 5; // 5 minutes

/**
 * Unified balances, either computed or a placeholder for loading state.
 */
export type UnifiedBalances =
  | ComputedUnifiedBalances
  | PlaceholderUnifiedBalances;

/**
 * Computed balances with various groupings and aggregations. See each key for detailed descriptions
 */
export type ComputedUnifiedBalances = {
  /**
   * Every individual balance, including the wallet they belong to
   * Nothing is summed up, nfts are included
   */
  balances: ChainAssetBalance[];

  /**
   * Every token balance, including the wallet they belong to
   * Nothing is summed up
   */
  tokenBalances: ChainTokenAssetBalance[];

  /**
   * Every nft balance, including the wallet they belong to
   * Nothing is summed up
   */
  nftBalances: ChainNftAssetBalance[];

  /**
   * Every position balance, including the wallet they belong to
   * Nothing is summed up
   */
  positionBalances: ChainPositionChainAssetBalance[];

  /**
   * Every collectible position (e.g. Collector Crypt cards), summed across
   * wallets. Surfaced here so the send/move asset picker can offer cards as a
   * transferable asset without re-deriving them from the position grouping.
   */
  collectibleBalances: ChainPositionChainAssetBalanceSummed[];

  /**
   * Collectible positions keyed by chain address id, for O(1) lookup of a
   * selected card's balance in the send/move review surfaces (cards are absent
   * from `balancesByChainAddressId`, which holds only token/nft balances).
   */
  collectibleBalancesByChainAddressId: Partial<
    Record<ChainAddressId, ChainPositionChainAssetBalanceSummed>
  >;

  /**
   * If any balance exists.
   * Shorthand for balances.length > 0
   */
  hasBalances: boolean;

  /**
   * If any nft balance exists.
   * Shorthand for nftBalances.length > 0
   */
  hasNftBalances: boolean;

  /**
   * If any position balance exists.
   * Shorthand for positionBalances.length > 0
   */
  hasPositionBalances: boolean;

  /**
   * If any collectible position exists.
   * Shorthand for collectibleBalances.length > 0
   */
  hasCollectibleBalances: boolean;

  /**
   * If any token balance exists.
   * Shorthand for tokenBalances.length > 0
   */
  hasTokenBalances: boolean;

  /**
   * Every nft balance excluding patrons
   * Nothing is summed up
   */
  nftsExcludingPatrons: ChainNftAssetBalanceSummed[];
  nftsExcludingPatronsByGroupId: NftBalancesByGroupId;

  /**
   * The balance of all patron nfts, summed across wallets if no custody specified.
   * each identifier is in the array
   */
  patronNfts: ChainNftAssetBalanceSummed | null;
  patronNftsGrouped: GroupedNftBalance | null;

  /**
   * The balance of all withdrawable patron nfts, summed across wallets if no custody specified.
   * each identifier is in the array
   */
  withdrawablePatronNfts: ChainNftAssetBalanceSummed | null;
  withdrawablePatronGrouped: GroupedNftBalance | null;

  /**
   * Balances by chain and address. This would be the primary way to access
   * ChainAsset balances, as it is the most granular data structure that still
   * sums balances across wallets (if no custody filter provided)
   *
   * Think Send/Move asset selection
   */
  balancesByChainAddressId: BalancesByChainAddressId;

  /**
   * Balances grouped by groupId. This is the primary way to access
   * grouped balances, each entry includes the total across chains and each
   * individual chain balance. Assets without groups are included with the chainAddressId as the key.
   *
   * Think Wallet Balance Display
   */
  balancesByGroupId: TokenBalancesByGroupId;

  /**
   * Position balances grouped by protocol, then by groupId.
   */
  positionBalancesByGroupIdByTypeByProtocolId: PositionBalancesByGroupIdByTypeByProtocolId;

  /**
   * Whether the balances are stale
   */
  isStale: boolean;

  /**
   * Whether the balances are loading
   */
  isLoading: false;
};

export type PlaceholderUnifiedBalances = {
  balances: undefined;
  balancesByChainAddressId: undefined;
  balancesByGroupId: undefined;
  collectibleBalances: undefined;
  collectibleBalancesByChainAddressId: undefined;
  hasBalances: undefined;
  hasCollectibleBalances: undefined;
  hasNftBalances: undefined;
  hasPositionBalances: undefined;
  hasTokenBalances: undefined;
  isLoading: true;
  isStale: undefined;
  nftBalances: undefined;
  nftsExcludingPatrons: undefined;
  nftsExcludingPatronsByGroupId: undefined;
  patronNfts: undefined;
  patronNftsGrouped: undefined;
  positionBalances: undefined;
  positionBalancesByGroupIdByTypeByProtocolId: undefined;
  tokenBalances: undefined;
  withdrawablePatronGrouped: undefined;
  withdrawablePatronNfts: undefined;
};

export function computeUnifiedBalances(
  balances: ChainAssetBalance[],
  accountAddresses: AccountAddress[],
  precomputedBalances: PrecomputedBalances
): ComputedUnifiedBalances {
  const positionBalances: ChainPositionChainAssetBalance[] = [];
  const nonPositionBalances: ChainNonPositionAssetBalance[] = [];
  const tokenBalances: ChainTokenAssetBalance[] = [];
  const nftBalances: ChainNftAssetBalance[] = [];

  for (const balance of balances) {
    if (balance.type === 'position') {
      positionBalances.push(balance);
    } else {
      nonPositionBalances.push(balance);

      if (balance.type === 'token') {
        tokenBalances.push(balance);
      } else if (balance.type === 'nft') {
        nftBalances.push(balance);
      }
    }
  }

  const nftsSummedMap = new Map<string, ChainNftAssetBalanceSummed>();
  for (const nft of nftBalances) {
    const existingBalance = nftsSummedMap.get(nft.id);
    if (existingBalance) {
      existingBalance.rawBalance = existingBalance.rawBalance.plus(
        nft.rawBalance
      );
      existingBalance.decimalBalance = existingBalance.decimalBalance.plus(
        nft.decimalBalance
      );
      existingBalance.balanceUsd =
        (existingBalance.balanceUsd ?? 0) + (nft.balanceUsd ?? 0);
      existingBalance.nftData = [
        ...(existingBalance.nftData ?? []),
        ...(nft.nftData ?? []),
      ];
      existingBalance.custody = addUniqueCustodyAddresses(
        existingBalance.custody,
        nft,
        accountAddresses
      );
      existingBalance.walletIds = addUniqueWalletIds(
        existingBalance.walletIds,
        nft,
        accountAddresses
      );
      existingBalance.sources = addUniqueSources(
        existingBalance.sources,
        nft,
        accountAddresses
      );
    } else {
      nftsSummedMap.set(nft.id, {
        ...nft,
        custody: addUniqueCustodyAddresses([], nft, accountAddresses),
        walletIds: addUniqueWalletIds([], nft, accountAddresses),
        sources: addUniqueSources([], nft, accountAddresses),
      });
    }
  }
  const nftsSummed = Array.from(nftsSummedMap.values());

  // Sum collectible positions (e.g. Collector Crypt cards) across wallets. Each
  // card's mint is unique to a single wallet, so summing is effectively 1:1 —
  // but we still accumulate custody/walletIds/sources so the picker knows which
  // wallet a card lives in. No NFT-style balance accumulation: a card is always 1.
  const collectiblesSummedMap = new Map<
    string,
    ChainPositionChainAssetBalanceSummed
  >();
  for (const position of positionBalances) {
    if (position.positionData.positionType !== 'collectible') continue;
    const existing = collectiblesSummedMap.get(position.id);
    if (existing) {
      existing.custody = addUniqueCustodyAddresses(
        existing.custody,
        position,
        accountAddresses
      );
      existing.walletIds = addUniqueWalletIds(
        existing.walletIds,
        position,
        accountAddresses
      );
      existing.sources = addUniqueSources(
        existing.sources,
        position,
        accountAddresses
      );
    } else {
      const { walletAddress: _walletAddress, ...positionWithoutWalletAddress } =
        position;
      collectiblesSummedMap.set(position.id, {
        ...positionWithoutWalletAddress,
        custody: addUniqueCustodyAddresses([], position, accountAddresses),
        walletIds: addUniqueWalletIds([], position, accountAddresses),
        sources: addUniqueSources([], position, accountAddresses),
      });
    }
  }
  const collectibleBalances = Array.from(collectiblesSummedMap.values());
  const collectibleBalancesByChainAddressId: Partial<
    Record<ChainAddressId, ChainPositionChainAssetBalanceSummed>
  > = {};
  for (const collectible of collectibleBalances) {
    collectibleBalancesByChainAddressId[collectible.id as ChainAddressId] =
      collectible;
  }

  const patronNfts = nftsSummed.find((b) => b.assetId === 'patron_nft') ?? null;
  const nftsExcludingPatrons = nftsSummed.filter(
    (b) => b.assetId !== 'patron_nft'
  );

  // Calculate withdrawable patron NFTs
  const withdrawablePatronsBalance =
    patronNfts?.nftData?.reduce(
      (acc, nft) => acc + (nft.withdrawable ? 1 : 0),
      0
    ) ?? 0;

  const withdrawablePatronNfts: ChainNftAssetBalanceSummed | null =
    patronNfts && withdrawablePatronsBalance
      ? (() => {
          const totalBalance = patronNfts.decimalBalance.toNumber();
          const hasBalance = totalBalance > 0;
          const ratio = hasBalance
            ? withdrawablePatronsBalance / totalBalance
            : 0;

          const scaleNumber = (value: number | null | undefined) =>
            hasBalance && value != null ? value * ratio : value;

          return {
            ...patronNfts,
            nftData: patronNfts.nftData?.filter((nft) => nft.withdrawable),
            // Exact withdrawable count — patron NFTs are decimals=0 integers;
            // ratio-scaling drifts for non-binary ratios (e.g. 1 of 3).
            rawBalance: BigNumber(withdrawablePatronsBalance),
            decimalBalance: BigNumber(withdrawablePatronsBalance),
            balanceUsd: scaleNumber(patronNfts.balanceUsd),
            balance24hChangeUsd: scaleNumber(patronNfts.balance24hChangeUsd),
          };
        })()
      : null;

  // Group balances by chain and address
  const balancesByChainAddressId: BalancesByChainAddressId = {};
  for (const balance of nonPositionBalances) {
    const { walletAddress: _walletAddress, ...balanceWithoutWalletAddress } =
      balance;
    const existingBalance =
      balancesByChainAddressId[balance.id as ChainAddressId];
    if (existingBalance) {
      existingBalance.rawBalance = existingBalance.rawBalance.plus(
        balance.rawBalance
      );
      existingBalance.decimalBalance = existingBalance.decimalBalance.plus(
        balance.decimalBalance
      );
      existingBalance.balanceUsd =
        (existingBalance.balanceUsd ?? 0) + (balance.balanceUsd ?? 0);
      existingBalance.custody = addUniqueCustodyAddresses(
        existingBalance.custody,
        balance,
        accountAddresses
      );
      existingBalance.walletIds = addUniqueWalletIds(
        existingBalance.walletIds,
        balance,
        accountAddresses
      );
      existingBalance.sources = addUniqueSources(
        existingBalance.sources,
        balance,
        accountAddresses
      );
    } else {
      balancesByChainAddressId[balance.id as ChainAddressId] = {
        ...balanceWithoutWalletAddress,
        custody: addUniqueCustodyAddresses([], balance, accountAddresses),
        walletIds: addUniqueWalletIds([], balance, accountAddresses),
        sources: addUniqueSources([], balance, accountAddresses),
      };
    }
  }

  // Group balances by group ID
  const balancesByGroupId = groupBalancesByGroupId(
    tokenBalances,
    accountAddresses
  );

  // Group position balances by protocol, then by group ID
  const positionBalancesByGroupIdByTypeByProtocolId =
    groupPositionsByProtocolIdByGroupId(positionBalances, accountAddresses);

  // Check if data is stale based on the oldest dataset timestamp
  const oldestTimestamp = Math.min(
    precomputedBalances.datasetTimestamps.chainBalances,
    precomputedBalances.datasetTimestamps.perps
  );
  const isStale =
    oldestTimestamp !== 0 && Date.now() - oldestTimestamp > BALANCES_STALE_TIME;

  return {
    balances: balances,
    balancesByChainAddressId,
    balancesByGroupId,
    collectibleBalances,
    collectibleBalancesByChainAddressId,
    hasBalances: balances.length > 0,
    hasCollectibleBalances: collectibleBalances.length > 0,
    hasNftBalances: nftBalances.length > 0,
    hasPositionBalances: positionBalances.length > 0,
    hasTokenBalances: tokenBalances.length > 0,
    isLoading: false,
    isStale,
    nftBalances,
    nftsExcludingPatrons,
    nftsExcludingPatronsByGroupId: groupBalancesByGroupId(
      nftBalances.filter((nft) => nft.assetId !== 'patron_nft'),
      accountAddresses
    ),
    patronNfts,
    patronNftsGrouped: mapSummedNftToGroupedNft(patronNfts),
    positionBalances,
    positionBalancesByGroupIdByTypeByProtocolId,
    tokenBalances,
    withdrawablePatronGrouped: mapSummedNftToGroupedNft(withdrawablePatronNfts),
    withdrawablePatronNfts,
  };
}

function mapSummedNftToGroupedNft(
  nft: ChainNftAssetBalanceSummed | null
): GroupedNftBalance | null {
  if (!nft) {
    return null;
  }

  return {
    id: nft.assetId ?? nft.id,
    groupId: nft.groupId,
    isOfficial: nft.isOfficial,
    name: nft.name,
    symbol: nft.symbol,
    imgUrl: nft.imgUrl,
    lstAssetMetadata: nft.lstAssetMetadata,
    decimalTotalBalance: nft.decimalBalance,
    chainAssetBalances: {
      [nft.chain]: nft,
    },
    custody: nft.custody,
    walletIds: nft.walletIds,
    sources: nft.sources,
    ...(nft.isRateLoading
      ? {
          isRateLoading: true as const,
          rateUsd: undefined,
          rateUsdChange24h: undefined,
          totalBalanceUsd: undefined,
          totalBalance24hChangeUsd: undefined,
        }
      : {
          isRateLoading: false as const,
          rateUsd: nft.rateUsd ?? null,
          rateUsdChange24h: nft.rateUsdChange24h ?? null,
          totalBalanceUsd: nft.balanceUsd ?? null,
          totalBalance24hChangeUsd: nft.balance24hChangeUsd ?? null,
        }),
  };
}
